import { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, Palette, Smartphone, Cloud, Rocket, ShoppingCart, LayoutDashboard, Box, Code, Check } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const iconMap = {
  globe: Globe,
  palette: Palette,
  smartphone: Smartphone,
  cloud: Cloud,
  rocket: Rocket,
  'shopping-cart': ShoppingCart,
  'layout-dashboard': LayoutDashboard,
  box: Box,
  code: Code,
};

const ServicesSection = ({ services, products }) => {
  const [activeTab, setActiveTab] = useState('services');

  return (
    <section id="services" className="section-spacing bg-[#02040a]" data-testid="services-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            What We Offer
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Services & Products
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From custom development to ready-to-use solutions, we've got you covered.
          </p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-12">
            <TabsList className="bg-white/5 p-1 rounded-full">
              <TabsTrigger
                value="services"
                className="rounded-full px-8 py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                data-testid="services-tab"
              >
                Services
              </TabsTrigger>
              <TabsTrigger
                value="products"
                className="rounded-full px-8 py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                data-testid="products-tab"
              >
                Products
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="services">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => {
                const Icon = iconMap[service.icon] || Code;
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="glass-card p-6 group hover:border-primary/50 transition-all duration-300"
                    data-testid={`service-card-${index}`}
                  >
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold mb-2">{service.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{service.description}</p>
                    {service.features?.length > 0 && (
                      <ul className="space-y-2">
                        {service.features.slice(0, 4).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check className="w-4 h-4 text-primary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="products">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, index) => {
                const Icon = iconMap[product.icon] || Box;
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="glass-card p-6 group hover:border-primary/50 transition-all duration-300"
                    data-testid={`product-card-${index}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <Icon className="w-7 h-7 text-secondary" />
                      </div>
                      {product.price && (
                        <span className="text-2xl font-heading font-bold text-primary">{product.price}</span>
                      )}
                    </div>
                    <h3 className="text-xl font-heading font-semibold mb-2">{product.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{product.description}</p>
                    {product.features?.length > 0 && (
                      <ul className="space-y-2">
                        {product.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check className="w-4 h-4 text-secondary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ServicesSection;
